/**
 * App links: a URL presented as a sub-app (见 21).
 *
 * A source that feeds the launcher projection, kept in its own contract for a
 * reason worth stating: delete app links entirely and the launcher projection +
 * rail layout still stand on their own. That is the test for whether the two
 * belong in the platform separately, and they pass it.
 *
 * **An app link is deliberately not a module.** A module id is an origin, a deep
 * link authority segment and a storage namespace (见 20 — it is a security
 * boundary, not a naming habit), and the sha256 + origin-allowlist gate in 见 12
 * exists because installing a module means moving code into this process. A
 * pasted URL is neither, so the two are unified one level *above* identity and
 * trust, in {@link LaunchItem}, never merged with each other.
 */
import type { Disposer } from './event';
import type { LaunchHosting } from './launcher-contract';

/** Cross-process channel for the app-link registry (trusted renderer only). */
export const APP_LINKS_CHANNEL = 'app-links';

/** EventBus event emitted when the merged app-link set changes. */
export const APP_LINKS_CHANGED_EVENT = 'platform.appLinks.changed';

/** Source id of the platform's own writable layer (the user's own links). */
export const APP_LINK_USER_SOURCE = 'user';

/**
 * Rail / settings mark when an app-link has no icon URL. Same glyph the
 * app-center list uses (`i-link-2`); the launcher projection always hands the
 * renderer an IconSpec, so a missing URL never becomes a first letter.
 */
export const APP_LINK_DEFAULT_ICON_ID = 'i-link-2';

/**
 * Rail-ready icon for an app-link: the image URL if present, otherwise the
 * default link mark. Kept here so every surface (rail, settings, list) answers
 * the same way without each one inventing a fallback.
 */
export function appLinkIconSpec(
  icon?: string,
): { type: 'resource'; light: string } | { type: 'builtin'; id: string } {
  const src = icon?.trim();
  return src
    ? { type: 'resource', light: src }
    : { type: 'builtin', id: APP_LINK_DEFAULT_ICON_ID };
}

/** What a caller supplies to create or edit a user link. */
export interface AppLinkInput {
  readonly name: string;
  readonly url: string;
  /**
   * Icon URL. Either an `https:` address or a small `data:` URI (the settings UI
   * downsamples a picked image into one — the platform has no non-module file
   * store to put bytes in, 见 07). Absent uses {@link APP_LINK_DEFAULT_ICON_ID}
   * on the rail, not the title's first letter.
   */
  readonly icon?: string;
  /** User-created links default to `inline`, which falls back to the built-in browser. */
  readonly hosting?: LaunchHosting;
}

/** One app link after merging the user layer with the registered providers. */
export interface AppLink {
  /** Stable within its source; the launcher key is derived from it. */
  readonly id: string;
  readonly sourceId: string;
  readonly name: string;
  readonly url: string;
  readonly icon?: string;
  readonly hosting: LaunchHosting;
  /** Only the user's own links can be edited or removed on the client. */
  readonly editable: boolean;
  readonly order: number;
  /**
   * Whether the rail shows this link before the user has decided (见 21). Same
   * meaning as `ProvidedLaunchItem.defaultPinned`, and the same limit: it is an
   * initial value the user layer overrides. Forcing presence stays with a
   * preset layer's `locked`, which no link source can set.
   */
  readonly defaultPinned?: boolean;
}

/** One link as a provider hands it over (no `editable` — providers are read-only). */
export interface ProvidedAppLink {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly icon?: string;
  readonly hosting?: LaunchHosting;
  readonly order?: number;
  /** See {@link AppLink.defaultPinned}. Absent means "not until the user says so". */
  readonly defaultPinned?: boolean;
}

/**
 * A pluggable, read-only source of app links (见 21), registered in-process by
 * whichever module owns the data — organisation-pushed links come from the
 * module that already holds workspace identity, so the platform never learns
 * what an organisation is.
 *
 * Shape mirrors `ScopeLayer`: an id, a priority, a read, and a change signal.
 */
export interface AppLinkProvider {
  readonly id: string;
  /** Higher shadows lower when two sources carry the same normalized URL. */
  readonly priority: number;
  list(): Promise<readonly ProvidedAppLink[]>;
  readonly onDidChange?: (listener: () => void) => Disposer;
}

/**
 * Resolves a name/icon for a URL (见 21). The seam exists so the manual path and
 * a future server-side unfurl are the same call site.
 *
 * Cross-runtime note for whoever implements the real one: the browser runtime's
 * host is a SharedWorker, so fetching a third-party page is CORS-bound there.
 * A server endpoint is the only answer that works on both runtimes.
 */
export interface AppLinkMetadataResolver {
  resolve(url: string): Promise<{ name?: string; icon?: string }>;
}

/** The no-op resolver: everything is entered by hand (the current behaviour). */
export const MANUAL_METADATA_RESOLVER: AppLinkMetadataResolver = {
  resolve: () => Promise.resolve({}),
};

/**
 * In-process module face for the merged app-link set (见 21). Same verbs as the
 * settings panel, minus `refresh` — a provider already signals `onDidChange`,
 * and a consumer does not poke the other sources.
 *
 * Available to `source` modules, mirroring `context.launcher`. Changes
 * broadcast {@link APP_LINKS_CHANGED_EVENT}; observe them with `context.onEvent`.
 */
export interface AppLinkApi {
  list(): Promise<AppLink[]>;
  add(input: AppLinkInput): Promise<AppLink>;
  update(id: string, patch: AppLinkInput): Promise<void>;
  remove(id: string): Promise<void>;
}

/** Trusted-renderer RPC for the app-link registry. */
export interface IAppLinkService extends AppLinkApi {
  /** Force every provider to re-read (e.g. a manual refresh in settings). */
  refresh(): Promise<void>;
}

/**
 * Normalize a URL for cross-source dedupe: lowercase scheme + host, drop a
 * default port and a trailing slash, keep everything else.
 *
 * Deliberately conservative — query and fragment are preserved, because two
 * entries differing only in query are routinely two different apps, and merging
 * them would silently hide one.
 */
export function normalizeAppLinkUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.endsWith('/')
      ? parsed.pathname.slice(0, -1)
      : parsed.pathname;
    return `${parsed.protocol.toLowerCase()}//${parsed.host.toLowerCase()}${path}${parsed.search}${parsed.hash}`;
  } catch {
    return url.trim();
  }
}

/** Whether a string is a URL the platform will open (`http`/`https` only). */
export function isSupportedAppLinkUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}
